import { useEffect, useMemo, useState } from "react";
import CategoryTag from "@/components/base/CategoryTag";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import SEO from "@/components/SEO";
import { canonical, collectionPageSchema } from "@/utils/seo";
import { getAllStores } from "@/api/content";
import type { StoreCard } from "@/types/content";

export default function StoresPage({ initialData }: { initialData?: StoreCard[] }) {
  const [stores, setStores] = useState<StoreCard[]>(initialData || []);
  const [loading, setLoading] = useState(!initialData || initialData.length === 0);
  const [activeCategory, setActiveCategory] = useState<string>("全部");
  const [sortBy, setSortBy] = useState<"rating" | "price">("rating");

  useEffect(() => {
    getAllStores().then((data) => {
      setStores(data);
      setLoading(false);
    });
  }, []);

  const categories = useMemo(() => {
    const cats = new Set(stores.map((s) => s.category));
    return ["全部", ...Array.from(cats)];
  }, [stores]);

  const filteredStores = useMemo(() => {
    let result = [...stores];

    if (activeCategory !== "全部") {
      result = result.filter((s) => s.category === activeCategory);
    }

    if (sortBy === "rating") {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === "price") {
      const parseMinPrice = (range: string): number => {
        const match = range.replace(/,/g, "").match(/\d+/);
        return match ? parseInt(match[0], 10) : 0;
      };
      result.sort((a, b) => parseMinPrice(a.priceRange) - parseMinPrice(b.priceRange));
    }

    return result;
  }, [stores, activeCategory, sortBy]);

  if (loading) {
    return <SectionSkeleton type="spinner" />;
  }

  return (
    <div className="w-full">
      <SEO
        meta={{
          title: "探店精選｜美好時光指南",
          description: "神秘客實地走訪，為你帶來最真實的店家評比與體驗心得，從按摩、SPA、美甲到美髮，幫你找到不踩雷的好店家。",
          canonical: canonical("/stores"),
          keywords: ["探店精選", "美業店家", "按摩推薦", "SPA評比", "美甲", "美好時光指南"],
          structuredData: collectionPageSchema("探店精選｜美好時光指南", "神秘客實地走訪，為你帶來最真實的店家評比與體驗心得。", "/stores"),
        }}
      />
      {/* Header */}
      <div className="w-full px-4 md:px-8 lg:px-12 pt-12 md:pt-16 pb-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
            探店精選
          </h1>
          <p className="text-slate-500 max-w-2xl">
            神秘客實地走訪，為你帶來最真實的店家評比與體驗心得
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="w-full border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 overflow-x-auto pb-1">
              <span className="text-sm font-medium text-slate-400 flex-shrink-0">
                分類：
              </span>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeCategory === cat
                      ? "bg-primary text-white"
                      : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm text-slate-400">排序：</span>
              <select
                value={sortBy}
                onChange={(e) =>
                  setSortBy(e.target.value as "rating" | "price")
                }
                className="text-sm bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:border-primary cursor-pointer"
              >
                <option value="rating">最高評分</option>
                <option value="price">價格最低</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
        <p className="text-sm text-slate-400">
          共 {filteredStores.length} 家探店店家
          {activeCategory !== "全部" && (
            <span className="ml-1">· 分類「{activeCategory}」</span>
          )}
          {sortBy === "price" && (
            <span className="ml-1">· 價格排序</span>
          )}
        </p>
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-12 md:pb-16">
        {filteredStores.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-slate-300">
              <i className="ri-search-line text-2xl" />
            </div>
            <p className="text-slate-500">沒有符合條件的探店店家</p>
            <button
              onClick={() => setActiveCategory("全部")}
              className="mt-2 text-primary text-sm hover:underline cursor-pointer"
            >
              清除篩選
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStores.map((store) => (
              <a
                key={store.id}
                href={`/stores/${store.slug}`}
                className="group block bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-primary/20 transition-all"
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    loading="lazy"
                    src={store.coverImage}
                    alt={store.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <CategoryTag label={store.category} variant="light" />
                  </div>
                </div>
                <div className="p-5">
                  <h2 className="text-lg font-semibold text-slate-800 mb-2 group-hover:text-primary transition-colors">
                    {store.name}
                  </h2>
                  <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                    {store.description}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-slate-400">
                      <i className="ri-star-fill text-amber-400" />
                      <span>{store.rating}</span>
                      <span>({store.reviewCount})</span>
                    </div>
                    <span className="text-primary font-medium text-sm">
                      {store.priceRange}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-400 mt-2">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-map-pin-line" />
                    </div>
                    <span>{store.location}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}